import React, { FC, useCallback, useMemo } from "react";
import { Image, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Modal, Text } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../../../domain/entities/product/Product";
import { OrderSummaryForCheckout } from "../../../../../../domain/projections/OrderSummaryForCheckout";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { cdnImageUrl } from "../../../../shared/util/cdnImageUrl";
import { style as stockOutModalStyle } from "./StockOutModal.styles";

const VISIBLE_ITEMS = 4;
const productImageWidth = 56;

interface StockOutModalProps {
  readonly visible: boolean;
  readonly orderSummary: OrderSummaryForCheckout;
  readonly onClose: () => void;
  readonly onDismiss: () => void;
  readonly onLoad: (stockoutItems: Product[]) => void;
  readonly onAcceptWhenEveryItemStockout: () => void;
  readonly onAcceptWhenSomeItemStockout: (stockoutItems: Product[]) => void;
}

const StockOutModal: FC<StockOutModalProps> = ({
  visible,
  orderSummary,
  onClose,
  onDismiss,
  onLoad,
  onAcceptWhenEveryItemStockout,
  onAcceptWhenSomeItemStockout,
}) => {
  const style = useMemo(() => stockOutModalStyle(), []);

  const notAvailableItems = useMemo(
    () => orderSummary.items.filter((item) => item.available === false),
    [orderSummary.items],
  );

  const allItemsAreStockout = notAvailableItems.length === orderSummary.items.length;

  const modalTitleText = useI18nMessage({
    domain: DOMAIN,
    id: allItemsAreStockout ? I18nMessages.STOCK_OUT_MODAL_NO_ITEMS_TITLE : I18nMessages.STOCK_OUT_MODAL_TITLE,
  });
  const stockOutModalNoItemsBodyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_MODAL_NO_ITEMS_BODY,
  });
  const stockOutModalBodyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_MODAL_BODY,
  });
  const stockOutModalButtonBackText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_MODAL_BUTTON_BACK,
  });
  const stockOutModalButtonCloseAndGoText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_MODAL_BUTTON_CLOSE_AND_GO,
  });
  const stockOutModalButtonDeleteAndPayText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.STOCK_OUT_MODAL_BUTTON_DELETE_AND_PAY,
  });

  const hasMoreProducts = notAvailableItems.length > VISIBLE_ITEMS;
  const moreProducts = notAvailableItems.length - VISIBLE_ITEMS;
  const products = hasMoreProducts ? notAvailableItems.slice(0, 4) : notAvailableItems;

  const handleOnAcceptWhenSomeItemStockout = useCallback(() => {
    onAcceptWhenSomeItemStockout(notAvailableItems);
  }, [notAvailableItems, onAcceptWhenSomeItemStockout]);

  const onLayout = useCallback(() => {
    onLoad?.(notAvailableItems);
  }, [notAvailableItems, onLoad]);

  return (
    <Modal portalHostName="LookIn" visible={visible} showCloseButton onClose={onClose}>
      <View style={style.modalContent} onLayout={onLayout}>
        <Text level={3} style={style.title} variant="heading">
          {modalTitleText}
        </Text>

        <View style={style.imagesContainer}>
          {products.map((product) => (
            <Image
              key={product.variantId}
              resizeMode="cover"
              src={cdnImageUrl({ imageUrl: product.imageUrl, width: productImageWidth })}
              style={style.image}
            />
          ))}

          {hasMoreProducts && (
            <View style={style.moreProductsContainer}>
              <Text level={1} style={style.moreProductsText} variant="detail">
                +{moreProducts}
              </Text>
            </View>
          )}
        </View>

        <Text level={1} style={style.moreProductsText} variant="detail">
          {allItemsAreStockout ? stockOutModalNoItemsBodyText : stockOutModalBodyText}
        </Text>

        <Button
          style={{ button: style.backButton }}
          testID="back-to-checkout-button"
          variant="secondary"
          onPress={onDismiss}
        >
          {stockOutModalButtonBackText}
        </Button>

        {allItemsAreStockout ? (
          <Button testID="close-and-go-shop-button" onPress={onAcceptWhenEveryItemStockout}>
            {stockOutModalButtonCloseAndGoText}
          </Button>
        ) : (
          <Button testID="remove-and-pay-button" onPress={handleOnAcceptWhenSomeItemStockout}>
            {stockOutModalButtonDeleteAndPayText}
          </Button>
        )}
      </View>
    </Modal>
  );
};

export { StockOutModal };
